import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowLeft, Upload, Loader2, Trash2, ToggleLeft, ToggleRight, Shield, FileAudio, FileText } from 'lucide-react';
import { toast } from 'sonner';

const AdminOralPassagesPage = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maxActive, setMaxActive] = useState(3);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // Upload form state
  const [title, setTitle] = useState('');
  const [level, setLevel] = useState('P4');
  const [passageText, setPassageText] = useState('');
  const [active, setActive] = useState(true);
  const [pdf, setPdf] = useState(null);
  const [audio, setAudio] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const data = await api.adminListOralPassages();
      setPassages(data.passages || []);
      setActiveCount(data.active_count || 0);
      setMaxActive(data.max_active || 3);
    } catch (e) {
      if (e.response?.status === 403) setAccessDenied(true);
      else toast.error('Failed to load passages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => {
    setTitle(''); setLevel('P4'); setPassageText(''); setActive(true); setPdf(null); setAudio(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!title.trim() || !passageText.trim() || !pdf || !audio) {
      toast.error('Please fill in all fields and attach both files.');
      return;
    }
    setUploading(true);
    try {
      await api.adminCreateOralPassage({
        title: title.trim(),
        level,
        passage_text: passageText.trim(),
        active,
        pdf,
        audio,
      });
      toast.success('Passage uploaded!');
      resetForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = async (p) => {
    try {
      await api.adminUpdateOralPassage(p.id, { active: !p.active });
      toast.success(!p.active ? 'Activated' : 'Deactivated');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete passage "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.adminDeleteOralPassage(p.id);
      toast.success('Deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (accessDenied) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F8FAFC]">
        <Card className="p-8 rounded-3xl max-w-md text-center border-2 border-red-200" data-testid="oral-admin-access-denied">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-red-700 mb-2">Access Denied</h1>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-8">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/admin')} variant="outline" size="sm" className="rounded-full" data-testid="oral-admin-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="oral-admin-title">Oral Reading Passages</h1>
              <p className="text-sm text-slate-500">{activeCount} of {maxActive} active</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-2 gap-6">
        {/* Upload form */}
        <Card className="rounded-3xl border-2 border-emerald-200 p-6 bg-white" data-testid="oral-admin-upload-form">
          <h2 className="text-lg font-heading font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
            Upload new passage
          </h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label className="text-xs font-bold text-slate-700">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The Coral Reef Adventure" className="mt-1" data-testid="oral-admin-title-input" />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Level</Label>
              <select value={level} onChange={(e) => setLevel(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="oral-admin-level-select">
                <option value="P4">P4</option>
                <option value="P5">P5</option>
                <option value="P6">P6</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Passage text (what the audio reads)</Label>
              <Textarea
                value={passageText}
                onChange={(e) => setPassageText(e.target.value)}
                rows={6}
                className="mt-1 font-mono text-sm"
                placeholder="Paste the exact reading passage here…"
                data-testid="oral-admin-passage-text"
              />
              <div className="text-[11px] text-slate-500 mt-1">{passageText.length} chars · min 20 required</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><FileText className="w-3.5 h-3.5"/> Passage PDF</Label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdf(e.target.files?.[0] || null)}
                  className="mt-1 text-xs w-full"
                  data-testid="oral-admin-pdf-input"
                />
                {pdf && <div className="text-[11px] text-emerald-700 mt-1 truncate">{pdf.name}</div>}
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 flex items-center gap-1"><FileAudio className="w-3.5 h-3.5"/> Reference audio</Label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAudio(e.target.files?.[0] || null)}
                  className="mt-1 text-xs w-full"
                  data-testid="oral-admin-audio-input"
                />
                {audio && <div className="text-[11px] text-emerald-700 mt-1 truncate">{audio.name}</div>}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="oral-admin-active-checkbox" />
              Activate now (visible to students immediately)
            </label>
            <Button
              type="submit"
              disabled={uploading}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-5 font-bold"
              data-testid="oral-admin-upload-button"
            >
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Uploading…</> : <><Upload className="w-4 h-4 mr-2"/>Upload passage</>}
            </Button>
          </form>
        </Card>

        {/* Passage list */}
        <div className="space-y-4">
          <h2 className="text-lg font-heading font-bold text-slate-800">Existing passages</h2>
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto"/></div>
          ) : passages.length === 0 ? (
            <Card className="p-8 rounded-3xl border-2 border-slate-200 text-center text-slate-500" data-testid="oral-admin-empty">
              No passages yet — upload your first one on the left.
            </Card>
          ) : (
            passages.map((p) => (
              <Card key={p.id} className={`rounded-2xl border-2 p-4 ${p.active ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`} data-testid={`oral-admin-passage-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700">{p.level}</span>
                      {p.active
                        ? <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-600 text-white px-2 py-0.5 rounded-full">Active</span>
                        : <span className="text-[10px] font-black uppercase tracking-wide bg-slate-300 text-slate-700 px-2 py-0.5 rounded-full">Inactive</span>}
                    </div>
                    <h3 className="text-base font-heading font-bold text-slate-800 truncate">{p.title}</h3>
                    <p className="text-xs text-slate-600 line-clamp-2 mt-1">{p.passage_text}</p>
                    <div className="text-[11px] text-slate-500 mt-2">
                      PDF: {p.pdf_filename} · Audio: {p.audio_filename}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleToggle(p)}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      data-testid={`oral-admin-toggle-${p.id}`}
                    >
                      {p.active ? <><ToggleRight className="w-4 h-4 mr-1 text-emerald-600"/>Deactivate</> : <><ToggleLeft className="w-4 h-4 mr-1"/>Activate</>}
                    </Button>
                    <Button
                      onClick={() => handleDelete(p)}
                      variant="outline"
                      size="sm"
                      className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50"
                      data-testid={`oral-admin-delete-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1"/>Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminOralPassagesPage;
